<template>
  <div class="carrossel-step-informacoes">
    <h3 class="step-title">Informações básicas</h3>
    <p class="step-subtitle">
      Informe os dados principais
      {{ productEditorMode ? "da vitrine" : "do carrossel" }}.
    </p>

    <v-divider class="mb-5" />

    <v-row>
      <v-col cols="12">
        <label class="field-label">{{ nomeFieldLabel }}</label>
        <FormField
          v-model="wizardState.carrossel.titulo"
          placeholder="Ex.: Promoção Outubro 2025"
          :validation="$v.wizardState.carrossel.titulo"
        />
        <small class="field-hint">
          Esse nome será usado apenas para identificação interna.
        </small>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <label class="field-label">Local de exibição</label>
        <FormAutocomplete
          v-model="wizardState.selectedLocalExibicao"
          placeholder="Selecione o local de exibição"
          multiple
          chips
          :options="localOptions"
          :validation="$v.wizardState.selectedLocalExibicao"
        />
        <small class="field-hint">
          Escolha onde {{ localHintSubject }} será exibido{{ localHintSuffix }}.
        </small>
      </v-col>

      <v-col cols="12" md="4">
        <label class="field-label">Data de início</label>
        <FormDatetimePicker
          format="dd/MM/yyyy HH:mm"
          label="Selecione a data de início"
          v-model="wizardState.carrossel.inicio_vigencia"
          :clearable="false"
        />
        <small class="field-hint">
          Data e hora em que {{ localHintSubject }} começará a ser exibido{{
            localHintSuffix
          }}.
        </small>
      </v-col>

      <v-col cols="12" md="4">
        <label class="field-label">Data final</label>
        <FormDatetimePicker
          format="dd/MM/yyyy HH:mm"
          label="Selecione a data final"
          v-model="wizardState.carrossel.fim_vigencia"
          :clearable="false"
          :validation="$v.wizardState.carrossel.fim_vigencia"
          :error-message="fimVigenciaErrorMessage"
        />
        <small class="field-hint">
          Data e hora em que {{ localHintSubject }} deixará de ser exibido{{
            localHintSuffix
          }}.
        </small>
      </v-col>
    </v-row>

    <v-row v-if="wizardState.showStaticPages">
      <v-col cols="12">
        <label class="field-label">Páginas</label>
        <FormAutocomplete
          v-model="wizardState.presentOnStaticPage"
          placeholder="Selecione as páginas"
          multiple
          chips
          :options="staticPageOptions"
          :validation="$v.wizardState.presentOnStaticPage"
        />
        <small class="field-hint">
          Selecione em quais páginas {{ localHintSubject }} aparecerá.
        </small>
      </v-col>
    </v-row>

    <v-row v-if="wizardState.showCategorias">
      <v-col cols="12">
        <label class="field-label">Categorias</label>
        <FormAutocomplete
          v-model="wizardState.selectedCategorias"
          placeholder="Selecione as categorias"
          multiple
          chips
          :options="categoriaOptions"
          :validation="$v.wizardState.selectedCategorias"
        />
        <small class="field-hint">
          Selecione em quais categorias {{ localHintSubject }} aparecerá.
        </small>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import * as moment from "moment";
import { requiredIf } from "vuelidate/lib/validators";
import FormField from "@/components/form/FormField";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import FormDatetimePicker from "@/components/form/FormDatetimePicker";
import {
  buildLocaleExibicao,
  getCategoriaLocalOptions,
  getLocalExibicaoBaseOptions,
  LOCAL_CATEGORIA_SENTINEL
} from "../../utils/utilCarrossel";

const acceptedStaticLocal = "page:static-page";

export default {
  name: "CarrosselStepInformacoes",
  components: {
    FormField,
    FormAutocomplete,
    FormDatetimePicker
  },
  props: {
    wizardState: {
      type: Object,
      required: true
    },
    setoresFolha: {
      type: Array,
      default: () => []
    },
    paginasEstaticas: {
      type: Array,
      default: () => []
    },
    productEditorMode: {
      type: Boolean,
      default: false
    }
  },
  validations() {
    return {
      wizardState: {
        carrossel: {
          titulo: {
            required(value) {
              return !!(value && String(value).trim());
            }
          },
          fim_vigencia: {
            required(value) {
              const inicio = this.wizardState.carrossel.inicio_vigencia;
              if (!inicio || !value) {
                return true;
              }
              return moment(value).isSameOrAfter(moment(inicio));
            }
          }
        },
        selectedLocalExibicao: {
          required(value) {
            return Array.isArray(value) && value.length > 0;
          }
        },
        presentOnStaticPage: {
          required: requiredIf(function() {
            return this.wizardState.showStaticPages;
          })
        },
        selectedCategorias: {
          required: requiredIf(function() {
            return this.wizardState.showCategorias;
          })
        }
      }
    };
  },
  computed: {
    nomeFieldLabel() {
      return this.productEditorMode ? "Nome da vitrine" : "Nome do carrossel";
    },
    localHintSubject() {
      return this.productEditorMode ? "a vitrine" : "o carrossel";
    },
    localHintSuffix() {
      return this.productEditorMode ? "a" : "";
    },
    localOptions() {
      return getLocalExibicaoBaseOptions(this.setoresFolha).filter(
        o => o.value
      );
    },
    categoriaOptions() {
      return getCategoriaLocalOptions(this.setoresFolha);
    },
    staticPageOptions() {
      return (this.paginasEstaticas || []).map(page => ({
        text: page.titulo,
        value: page.idconteudoestatico
      }));
    },
    fimVigenciaErrorMessage() {
      return {
        required: "A data final deve ser posterior à data de início"
      };
    }
  },
  watch: {
    "wizardState.selectedLocalExibicao": {
      deep: true,
      handler(selected) {
        this.handleLocalChange(selected);
      }
    },
    "wizardState.selectedCategorias": {
      deep: true,
      handler() {
        this.syncLocaleExibicao();
        this.$emit("dirty");
      }
    },
    "wizardState.carrossel.inicio_vigencia"() {
      if (this.$v.wizardState.carrossel.fim_vigencia.$dirty) {
        this.$v.wizardState.carrossel.fim_vigencia.$touch();
      }
    }
  },
  methods: {
    handleLocalChange(selected = []) {
      const values = (selected || []).map(item =>
        typeof item === "string" ? item : item?.value
      );
      const hasStatic = values.includes(acceptedStaticLocal);
      const hasCategoria = values.includes(LOCAL_CATEGORIA_SENTINEL);

      this.wizardState.showStaticPages = hasStatic;
      this.wizardState.showCategorias = hasCategoria;

      if (!hasStatic) {
        if (this.wizardState.carrossel?.config) {
          this.wizardState.carrossel.config.presentOnStaticPage = [];
        }
        this.wizardState.presentOnStaticPage = [];
        this.$v.wizardState.presentOnStaticPage.$reset();
      }

      if (!hasCategoria) {
        this.wizardState.selectedCategorias = [];
        this.$v.wizardState.selectedCategorias.$reset();
      }

      this.syncLocaleExibicao();
      this.$emit("dirty");
    },
    syncLocaleExibicao() {
      this.wizardState.carrossel.localexibicao = buildLocaleExibicao(
        this.wizardState.selectedLocalExibicao,
        this.wizardState.selectedCategorias
      );
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    syncToCarrossel() {
      if (!this.wizardState.carrossel.config) {
        this.wizardState.carrossel.config = {};
      }

      this.syncLocaleExibicao();
      this.wizardState.carrossel.config.presentOnStaticPage =
        this.wizardState.presentOnStaticPage || [];
    }
  }
};
</script>

<style scoped>
.step-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: left;
}

.step-subtitle {
  color: #666;
  margin-bottom: 24px;
  text-align: left;
}

.field-label {
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
  margin-left: 10px;
  text-align: left;
  width: 100%;
}

.field-hint {
  display: block;
  color: #888;
  font-size: 12px;
  margin-top: -8px;
  margin-left: 10px;
  margin-bottom: 10px;
  text-align: left;
}
</style>
